import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import PropTypes from 'prop-types'
import React from 'react'

import {
  allPermissionIds,
  descendantIds,
  permissionTree,
  togglePermission,
  totalPermissions
} from 'app/permissions'
import { colors } from 'app/theme/colors'

import PermissionTree from './PermissionTree'

const useStyles = makeStyles((theme) => ({
  content: { paddingTop: theme.spacing(1) },
  fields: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  permissionsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  bulkActions: { display: 'flex', gap: theme.spacing(1) },
  search: { width: 240 },
  tree: {
    maxHeight: 380,
    overflow: 'auto',
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 8,
    padding: theme.spacing(1.5)
  },
  hint: { color: colors.textTertiary, marginTop: theme.spacing(1) },
  empty: { color: colors.textTertiary, padding: theme.spacing(2) }
}))

// Keep a node when it matches, when an ancestor matched (whole subtree comes
// along), or when a descendant matched (so the path to the hit stays visible).
const filterTree = (nodes, query) => {
  if (!query) return nodes
  const needle = query.trim().toLowerCase()
  const keep = (node) => {
    if (node.label.toLowerCase().includes(needle)) return node
    const children = node.children.map(keep).filter(Boolean)
    return children.length ? { ...node, children } : null
  }
  return nodes.map(keep).filter(Boolean)
}

const collectIds = (nodes) =>
  nodes.flatMap((node) => [node.id, ...descendantIds(node)])

const RoleFormModal = ({ open, role, saving, onClose, onSave }) => {
  const classes = useStyles()
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState(new Set())
  const [expandedIds, setExpandedIds] = React.useState(new Set())
  const [query, setQuery] = React.useState('')

  // Super Admin holds everything by definition, including sections added later,
  // so its permissions are not a stored list and there is nothing to tick. Show
  // the tree fully checked and locked rather than pretending it is editable.
  const isSuperAdmin = Boolean(role?.is_super_admin)

  // Reset the form each time the dialog opens (create = blank, edit = the role).
  React.useEffect(() => {
    if (!open) return
    setName(role ? role.name : '')
    setDescription(role ? role.description : '')
    setSelectedIds(
      new Set(
        role?.is_super_admin
          ? allPermissionIds
          : (role && role.permissions) || []
      )
    )
    setExpandedIds(new Set())
    setQuery('')
  }, [open, role])

  const visibleTree = React.useMemo(
    () => filterTree(permissionTree, query),
    [query]
  )

  // A search should reveal its hits without the user expanding every branch.
  React.useEffect(() => {
    if (query) setExpandedIds(new Set(collectIds(visibleTree)))
  }, [query, visibleTree])

  const handleToggle = (id, checked) =>
    setSelectedIds((current) => togglePermission(current, id, checked))

  const handleExpand = (id, expanded) =>
    setExpandedIds((current) => {
      const next = new Set(current)
      if (expanded) next.add(id)
      else next.delete(id)
      return next
    })

  const expandAll = () => setExpandedIds(new Set(allPermissionIds))
  const collapseAll = () => setExpandedIds(new Set())
  const selectAll = () => setSelectedIds(new Set(allPermissionIds))
  const clearAll = () => setSelectedIds(new Set())

  const handleSave = () =>
    onSave({
      ...(role || {}),
      name: name.trim(),
      description: description.trim(),
      // Super Admin's access comes from its flag, not from this list — don't
      // write the whole catalogue onto it just because the tree renders ticked.
      permissions: isSuperAdmin
        ? (role && role.permissions) || []
        : [...selectedIds]
    })

  const canSave =
    name.trim().length > 0 && (isSuperAdmin || selectedIds.size > 0) && !saving

  // A duplicated role arrives pre-filled but with no uuid — that's a create, not
  // an edit. (Roles are keyed by `uuid` server-side, not `id`.)
  const isEdit = Boolean(role && role.uuid)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit role · ${role.name}` : 'Create a new role'}
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.content}>
        <Box className={classes.fields}>
          <TextField
            label="Role name"
            placeholder="e.g. Cluster Manager"
            value={name}
            onChange={(event) => setName(event.target.value)}
            variant="outlined"
            size="small"
            required
            style={{ width: 260 }}
          />
          <TextField
            label="Description"
            placeholder="What is this role allowed to do?"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Box>

        <Box className={classes.permissionsHeader}>
          <Box>
            <Typography variant="subtitle2">Permissions</Typography>
            <Typography
              variant="caption"
              style={{ color: colors.textSecondary }}
            >
              {isSuperAdmin
                ? 'Every tab, including any added in future'
                : `${selectedIds.size} of ${totalPermissions} tabs selected`}
            </Typography>
          </Box>
          <Box className={classes.bulkActions}>
            <TextField
              className={classes.search}
              placeholder="Search tabs"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Button size="small" onClick={expandAll}>
              Expand all
            </Button>
            <Button size="small" onClick={collapseAll}>
              Collapse all
            </Button>
            {!isSuperAdmin && (
              <>
                <Button size="small" color="primary" onClick={selectAll}>
                  Select all
                </Button>
                <Button size="small" onClick={clearAll}>
                  Clear
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box className={classes.tree}>
          {visibleTree.length ? (
            <PermissionTree
              nodes={visibleTree}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              disabled={isSuperAdmin}
              onToggle={handleToggle}
              onExpand={handleExpand}
            />
          ) : (
            <Typography variant="body2" className={classes.empty}>
              No tabs match &ldquo;{query}&rdquo;.
            </Typography>
          )}
        </Box>

        <Typography variant="caption" className={classes.hint} component="p">
          {isSuperAdmin
            ? 'Super Admin always has every section, including ones added later — its permissions cannot be edited.'
            : 'Ticking a section grants every tab nested under it. Untick individual tabs to carve out exceptions. This controls what Alfred shows, not what the APIs allow.'}
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={!canSave}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create role'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

RoleFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  role: PropTypes.object, // null => create mode
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}

RoleFormModal.defaultProps = {
  role: null,
  saving: false
}

export default RoleFormModal
